import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { ArrowRight, ArrowDown, ArrowLeftRight, Loader2, Landmark, Check } from 'lucide-react';
import { toast } from 'sonner';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '../ui/dialog';
import { Button } from '../ui/button';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SIGNAL_LABEL = {
  linked_account: 'Linked wallet/savings',
  own_name: 'Your name on it',
  other_bank_named: 'Other bank named',
  exact_amount: 'Exact amount',
  same_day: 'Same day',
  transfer_keyword: 'Transfer wording',
};

const StatusBadge = ({ status }) => (
  status === 'review_needed'
    ? <span className="inline-flex px-1.5 py-0.5 rounded text-[9px] font-bold bg-amber-100 text-amber-700 border border-amber-300">REVIEW NEEDED</span>
    : <span className="inline-flex px-1.5 py-0.5 rounded text-[9px] font-bold bg-emerald-100 text-emerald-700 border border-emerald-300">COMPLETED</span>
);

const accountLabel = (leg) => {
  const bank = leg.bank_name || 'Bank';
  const acct = leg.account_name || '';
  const name = !acct || acct === bank ? bank : acct.toLowerCase().includes(bank.toLowerCase()) ? acct : `${bank} · ${acct}`;
  return `${name}${leg.account_last4 ? ` ••${leg.account_last4}` : ''}`;
};

const LegCard = ({ leg, label, currencySymbol }) => (
  <div className="flex-1 min-w-0 w-full border rounded-lg p-3 bg-slate-50 overflow-hidden">
    <div className="flex items-center justify-between gap-2 mb-1.5">
      <span className="text-[10px] font-semibold uppercase tracking-wide text-slate-500">{label}</span>
      <StatusBadge status={leg.status} />
    </div>
    <p className="text-sm font-bold text-slate-800">{currencySymbol}{Number(leg.amount).toLocaleString()}</p>
    <p className="text-[11px] text-slate-600 flex items-start gap-1 mt-0.5 min-w-0">
      <Landmark className="w-3 h-3 shrink-0 mt-0.5" />
      <span className="min-w-0 break-words [overflow-wrap:anywhere]">{accountLabel(leg)} · {leg.date}</span>
    </p>
    <p className="text-[11px] text-slate-500 mt-1 line-clamp-2 break-words [overflow-wrap:anywhere]" title={leg.description}>{leg.description || '(no narration)'}</p>
    {leg.recorded_as && (
      <p className="text-[10px] mt-1.5 text-slate-600">Currently recorded as: <span className="font-semibold">{leg.recorded_as}</span></p>
    )}
  </div>
);

const CandidateRow = ({ c, selected, onSelect, currencySymbol }) => (
  <button
    type="button"
    onClick={() => onSelect(c.tx_id)}
    className={`w-full min-w-0 overflow-hidden text-left rounded-md border px-2.5 py-2 transition-colors ${selected ? 'border-indigo-500 bg-indigo-50' : 'border-slate-200 hover:bg-slate-50'}`}
    data-testid={`transfer-candidate-${c.tx_id}`}
  >
    <div className="flex flex-wrap items-center justify-between gap-x-2 gap-y-1">
      <span className="text-xs font-semibold text-slate-800">{currencySymbol}{Number(c.amount).toLocaleString()} · {c.date}</span>
      <span className="flex items-center gap-1 text-[10px] shrink-0">
        {c.is_suggested && <span className="px-1.5 py-0.5 rounded bg-indigo-600 text-white font-semibold">Best match</span>}
        {c.confidence && <span className={`px-1.5 py-0.5 rounded border ${c.confidence === 'high' ? 'bg-emerald-50 text-emerald-700 border-emerald-200' : 'bg-amber-50 text-amber-700 border-amber-200'}`}>{c.confidence}</span>}
        {selected && <Check className="w-3.5 h-3.5 text-indigo-600" />}
      </span>
    </div>
    <p className="text-[11px] text-slate-600 mt-0.5 line-clamp-2 break-words [overflow-wrap:anywhere]">{accountLabel(c)} · {c.description || '(no narration)'}</p>
    {c.signals?.length > 0 && (
      <p className="text-[10px] text-slate-500 mt-0.5 break-words">{c.signals.map((s) => SIGNAL_LABEL[s] || s).join(' · ')}</p>
    )}
  </button>
);

/**
 * TransferPairConfirmDialog - the ONLY way to record a transfer between own
 * accounts from a bank-feed row. Shows the best match pre-selected, lists the
 * other possible legs (scored by the same engine) so the user can pick, and
 * only executes on explicit confirmation.
 */
export const TransferPairConfirmDialog = ({ tx, currencySymbol = '₦', open, onOpenChange, onDone }) => {
  const [candidates, setCandidates] = useState(null);
  const [selectedId, setSelectedId] = useState(null);
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) { setCandidates(null); setSelectedId(null); setPreview(null); return; }
    setLoading(true);
    axios.get(`${API}/bank-feeds/transfer-pairs/candidates/${tx.id}`)
      .then((r) => {
        const list = r.data?.candidates || [];
        setCandidates(list);
        const best = list.find((c) => c.is_suggested) || list[0];
        setSelectedId(best?.tx_id || null);
      })
      .catch((err) => {
        toast.error(err?.response?.data?.detail || 'Could not look for matching transactions');
        onOpenChange(false);
      })
      .finally(() => setLoading(false));
  }, [open, tx.id]);

  useEffect(() => {
    if (!open || !selectedId) { setPreview(null); return; }
    let cancelled = false;
    axios.get(`${API}/bank-feeds/transfer-pairs/preview/${tx.id}`, { params: { other_tx_id: selectedId } })
      .then((r) => { if (!cancelled) setPreview(r.data); })
      .catch((err) => { if (!cancelled) { setPreview(null); toast.error(err?.response?.data?.detail || 'Could not preview this match'); } });
    return () => { cancelled = true; };
  }, [open, selectedId, tx.id]);

  const confirm = async () => {
    if (!selectedId) return;
    setSaving(true);
    try {
      const body = { tx_id: tx.id, other_tx_id: selectedId };
      let r = await axios.post(`${API}/bank-feeds/transfer-pairs/confirm`, body);
      if (r.data?.requires_undo) {
        await axios.post(`${API}/bank-feeds/undo-classification/${r.data.requires_undo}`, {
          reason: 'User confirmed transfer between own bank accounts',
          delete_documents: true,
          delete_schedules: true,
        });
        r = await axios.post(`${API}/bank-feeds/transfer-pairs/confirm`, body);
      }
      if (r.data?.success) {
        toast.success(r.data.message || 'Transfer confirmed: both legs completed.');
        onOpenChange(false);
        onDone?.();
      } else {
        toast.error(r.data?.error || 'Could not confirm transfer');
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Action failed');
    } finally { setSaving(false); }
  };

  const notATransfer = async () => {
    setSaving(true);
    try {
      const r = await axios.post(`${API}/bank-feeds/transfer-pairs/dismiss`, { tx_id: tx.id });
      toast.success(r.data?.message || 'Marked as not a transfer');
      onOpenChange(false);
      onDone?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Action failed');
    } finally { setSaving(false); }
  };

  const noCandidates = !loading && candidates && candidates.length === 0;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="w-[calc(100vw-1.5rem)] sm:w-full max-w-xl max-h-[90vh] overflow-y-auto overflow-x-hidden p-4 sm:p-6 space-y-3" data-testid="transfer-pair-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-base">
            <ArrowLeftRight className="w-4 h-4 text-indigo-600" /> Transfer between your accounts?
          </DialogTitle>
          <DialogDescription className="text-xs text-left">
            Pick the other side of this transfer. Nothing is posted or changed until you confirm.
          </DialogDescription>
        </DialogHeader>

        {loading || !candidates ? (
          <div className="flex justify-center py-8"><Loader2 className="w-5 h-5 animate-spin text-slate-400" /></div>
        ) : noCandidates ? (
          <div className="text-sm text-slate-700 p-3 rounded-md bg-amber-50 border border-amber-200" data-testid="transfer-no-candidates">
            No matching money-{tx.direction === 'outflow' ? 'in' : 'out'} was found on your other bank accounts within 3 days
            {tx.direction === 'outflow' ? ' after' : ' before'} {String(tx.date).slice(0, 10)}. If the other account&apos;s statement
            hasn&apos;t been uploaded or synced yet, do that first and the match will appear here. Otherwise this is probably not a
            transfer between your own accounts.
          </div>
        ) : (
          <>
            {preview ? (
              <div className="flex flex-col sm:flex-row sm:items-stretch gap-2 min-w-0">
                <LegCard leg={preview.sender} label="Money out" currencySymbol={currencySymbol} />
                <div className="flex items-center justify-center shrink-0">
                  <ArrowDown className="w-4 h-4 text-indigo-400 sm:hidden" />
                  <ArrowRight className="w-4 h-4 text-indigo-400 hidden sm:block" />
                </div>
                <LegCard leg={preview.receiver} label="Money in" currencySymbol={currencySymbol} />
              </div>
            ) : (
              <div className="flex justify-center py-4"><Loader2 className="w-4 h-4 animate-spin text-slate-400" /></div>
            )}
            {preview?.fee > 0 && (
              <p className="text-[11px] text-slate-600 break-words">
                Amount difference of <strong>{currencySymbol}{Number(preview.fee).toLocaleString()}</strong> will be posted to <strong>Bank Charges</strong> (transfer fee).
              </p>
            )}
            {preview?.explanation && (
              <div className="text-[11px] p-2.5 rounded-md bg-indigo-50 border border-indigo-200 text-indigo-900 break-words" data-testid="transfer-pair-explanation">
                <span className="font-semibold">What happens if you confirm: </span>{preview.explanation}
              </div>
            )}
            <div className="space-y-1.5 min-w-0" data-testid="transfer-candidates-list">
              <p className="text-[11px] font-semibold text-slate-600 uppercase tracking-wide">
                {candidates.length > 1 ? `Possible matches (${candidates.length})` : 'Matching transaction'}
              </p>
              {candidates.map((c) => (
                <CandidateRow key={c.tx_id} c={c} selected={c.tx_id === selectedId} onSelect={setSelectedId} currencySymbol={currencySymbol} />
              ))}
            </div>
          </>
        )}

        <DialogFooter className="flex flex-col-reverse sm:flex-row sm:justify-end gap-2 pt-1">
          <Button variant="outline" size="sm" className="w-full sm:w-auto" onClick={() => onOpenChange(false)} disabled={saving} data-testid="transfer-pair-cancel-btn">Cancel</Button>
          <Button variant="ghost" size="sm" className="w-full sm:w-auto text-slate-600" onClick={notATransfer} disabled={saving || loading} data-testid="transfer-pair-not-transfer-btn">
            None of these — not a transfer
          </Button>
          {!noCandidates && (
            <Button size="sm" className="w-full sm:w-auto bg-indigo-600 hover:bg-indigo-700" onClick={confirm} disabled={loading || saving || !preview || !selectedId} data-testid="transfer-pair-authorize-btn">
              {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : 'Yes. These are the same transfer'}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
